import { useQuery } from '@tanstack/react-query'
import { apiClient } from '../../api/client'
import type { components } from '../../api/schema'

export type ThematicView = 'stacking' | 'floor' | 'site'
export type ThematicCatalogue = components['schemas']['ThematicCatalogue']
export type ThematicEntry = components['schemas']['ThematicEntry']
export type ThematicLegendData = components['schemas']['ThematicLegend']
export type ThematicClass = components['schemas']['ThematicClass']
export type ThematicBucket = components['schemas']['ThematicBucket']

const BASE = '/api/v1/organizations/{orgCode}/assets/{assetUid}/thematics' as const

/** GET what a view can be coloured by (suggestions first, then every eligible entry). */
export function useThematicCatalogue(org: string, assetUid: string | undefined, view: ThematicView) {
  return useQuery({
    queryKey: ['thematics', 'catalogue', org, assetUid, view],
    queryFn: async () => {
      const { data, error } = await apiClient.GET(BASE, {
        params: { path: { orgCode: org, assetUid: assetUid as string }, query: { view } },
      })
      if (error) throw error
      return data as ThematicCatalogue
    },
    enabled: !!org && !!assetUid,
  })
}

/** GET the legend of one thematic. `key` null means "no thematic". */
export function useThematicLegend(
  org: string,
  assetUid: string | undefined,
  view: ThematicView,
  key: string | null,
) {
  return useQuery({
    queryKey: ['thematics', 'legend', org, assetUid, view, key],
    queryFn: async () => {
      const { data, error } = await apiClient.GET(`${BASE}/{key}/legend` as '/api/v1/organizations/{orgCode}/assets/{assetUid}/thematics/{key}/legend', {
        params: { path: { orgCode: org, assetUid: assetUid as string, key: key as string }, query: { view } },
      })
      if (error) throw error
      return data as ThematicLegendData
    },
    enabled: !!org && !!assetUid && !!key,
    retry: false,
  })
}
